import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { ConstructionProgressResponse } from "@/adapters/api";
import {
	formatDayLabel,
	formatPercent,
	formatPrecisePercent,
} from "../../utils/format.ts";
import { Chart } from "../chart/index.tsx";

export interface ProgressCurveProps {
	data?: ConstructionProgressResponse;
	isPending: boolean;
	isError: boolean;
}

export function ProgressCurve({
	data,
	isPending,
	isError,
}: ProgressCurveProps) {
	const curve = data?.curve ?? [];
	const referenceDate = data?.reference_date;

	return (
		<Chart
			label="Curva S de avanço acumulado: planejado e realizado por período"
			height={280}
			isPending={isPending}
			isError={isError}
			isEmpty={curve.length === 0}
			table={{
				columns: ["Período", "Planejado", "Realizado"],
				rows: curve.map((point) => [
					point.label,
					formatPrecisePercent(point.planned_percent),
					point.actual_percent === null
						? "—"
						: formatPrecisePercent(point.actual_percent),
				]),
			}}
		>
			<LineChart data={curve} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
				<CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
				<XAxis dataKey="label" tickLine={false} axisLine={false} />
				<YAxis
					tickFormatter={(value) => formatPercent(Number(value))}
					tickLine={false}
					axisLine={false}
					domain={[0, 100]}
				/>
				<Tooltip
					formatter={(value, name, item) => {
						const formatted =
							value === null || value === undefined
								? "—"
								: formatPrecisePercent(Number(value));
						const asOf = item.payload?.actual_as_of;
						if (
							item.dataKey === "actual_percent" &&
							asOf &&
							asOf === referenceDate
						) {
							return [formatted, `${name} (até ${formatDayLabel(asOf)})`];
						}
						return formatted;
					}}
				/>
				<Legend />
				<Line
					type="monotone"
					dataKey="planned_percent"
					name="Planejado"
					stroke="var(--chart-3)"
					strokeWidth={2}
					strokeDasharray="4 4"
					dot={false}
				/>
				<Line
					type="monotone"
					dataKey="actual_percent"
					name="Realizado"
					stroke="var(--chart-1)"
					strokeWidth={2}
					dot={{ r: 4 }}
					activeDot={{ r: 5 }}
					connectNulls={false}
				/>
			</LineChart>
		</Chart>
	);
}
